import { useCallback, useEffect } from 'react';

const UNTHREAD_BASE_URL = 'https://spotify.unthread.io';
const UNTHREAD_WIDGET_ID = '71d1916f-d674-414e-b3f3-283d13de12f3';

interface UnthreadInAppChatSettings {
  baseUrl: string;
  widgetId: string;
}

interface UnthreadQueue {
  (...args: unknown[]): void;
  q: unknown[][];
  c: (args: unknown[]) => void;
}

declare global {
  interface Window {
    $unthreadInAppChatSettings?: UnthreadInAppChatSettings;
    $unthread?: {
      inAppChat?: UnthreadQueue;
    };
  }
}

interface UseUnthreadOptions {
  enabled?: boolean;
}

export const useUnthread = ({ enabled = true }: UseUnthreadOptions = {}) => {
  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    if (!UNTHREAD_BASE_URL || !UNTHREAD_WIDGET_ID) {
      // eslint-disable-next-line no-console
      console.warn('Unthread credentials not configured');
      return undefined;
    }

    if (window.$unthread?.inAppChat) {
      return undefined;
    }

    window.$unthreadInAppChatSettings = {
      baseUrl: UNTHREAD_BASE_URL,
      widgetId: UNTHREAD_WIDGET_ID,
    };

    const u = function queueFunction(this: UnthreadQueue, ...args: unknown[]) {
      this.c(args);
    } as UnthreadQueue;
    u.q = [];
    u.c = (args: unknown[]) => {
      u.q.push(args);
    };

    if (!window.$unthread) {
      window.$unthread = {};
    }
    window.$unthread.inAppChat = u;

    const script = document.createElement('script');
    script.src = `${UNTHREAD_BASE_URL}/widget/js/wrapper.js`;
    script.defer = true;
    script.async = true;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode?.insertBefore(script, firstScript);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [enabled]);

  const openChat = useCallback(
    (user?: { name: string; email: string }) => {
      if (!enabled) return;

      if (!UNTHREAD_BASE_URL || !UNTHREAD_WIDGET_ID) {
        // eslint-disable-next-line no-console
        console.warn(
          'Unthread credentials not configured. Please add your base URL and widget ID.',
        );
        return;
      }

      if (window.$unthread?.inAppChat) {
        if (user) {
          window.$unthread.inAppChat('start', { user });
        } else {
          window.$unthread.inAppChat('start');
        }
        window.$unthread.inAppChat('toggle');
      } else {
        // eslint-disable-next-line no-console
        console.warn('Unthread not loaded yet');
      }
    },
    [enabled],
  );

  const closeChat = useCallback(() => {
    if (!enabled) return;
    if (window.$unthread?.inAppChat) {
      window.$unthread.inAppChat('close');
    }
  }, [enabled]);

  const toggleChat = useCallback(() => {
    if (!enabled) return;
    if (window.$unthread?.inAppChat) {
      window.$unthread.inAppChat('toggle');
    }
  }, [enabled]);

  return {
    openChat,
    closeChat,
    toggleChat,
    isConfigured: enabled && !!(UNTHREAD_BASE_URL && UNTHREAD_WIDGET_ID),
  };
};
